import { useCallback, useEffect, useRef, useState } from "react";
import { Card, Form } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AdminButton } from "../../SuperAdmin/SharedComponents/ListView";
import Overlay from "../../SharedComponents/Overlay";
import CusProLogo from "../../Auth/Components/CusProLogo";
import { logOut } from "../../../Helpers/AuthHelper";
import { contractApi, CONTRACTS_PENDING_EVENT } from "../API/ContractAPI";
import SafeHtml from "./SafeHtml";

/** Pending contracts grouped by the company they belong to, in the order received. */
const groupByCompany = (items) => {
  const groups = [];
  items.forEach((c) => {
    let group = groups.find((g) => g.companyId === c.companyId);
    if (!group) groups.push((group = { companyId: c.companyId, companyName: c.companyName, items: [] }));
    group.items.push(c);
  });
  return groups;
};

/**
 * Full-screen blocker for company users and candidates: until they accept every contract that
 * currently applies to them they can't use the app, and the backend refuses their other API
 * calls. Re-checks on each navigation and whenever the backend reports pending contracts, so a
 * contract added or changed mid-session is caught too.
 *
 * Always rendered by AppLayout (`enabled` says whether it applies), so switching it on never
 * changes the shape of the tree and remounts the page. The page is first mounted only once the
 * answer is "nothing pending" (pages like a test start a session on mount); after that it stays
 * mounted, inert, underneath the blocker, so a mid-session contract change loses nothing.
 */
export default function PendingContractsGate({ enabled, user, children }) {
  const { t } = useTranslation();
  const location = useLocation();
  const token = user?.token;

  const [status, setStatus] = useState("loading"); // loading | clear | pending | error
  const [pageMounted, setPageMounted] = useState(false);
  const [items, setItems] = useState([]);
  const [checked, setChecked] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const latestCheck = useRef(0);

  // A different login starts over.
  useEffect(() => {
    setStatus("loading");
    setPageMounted(false);
  }, [token]);

  const check = useCallback(() => {
    if (!token) return Promise.resolve();
    const id = ++latestCheck.current;
    return contractApi.pending(token).then((r) => {
      if (id !== latestCheck.current) return; // a newer check is under way; its answer wins
      if (!r.ok) {
        // Only block on an error if we never got a clean answer; don't yank a page away
        // from someone who already passed because a background re-check failed.
        setStatus((prev) => (prev === "clear" ? prev : "error"));
        setError(r.message || t("contractLoadFailed"));
        return;
      }
      const pending = r.data || [];
      setItems(pending);
      setError(null);
      setStatus(pending.length ? "pending" : "clear");
      if (!pending.length) setPageMounted(true);
    });
  }, [token, t]);

  useEffect(() => {
    if (enabled) check();
  }, [enabled, check, location.pathname]);

  // The backend answers any other call with "contracts pending" once a contract is added or
  // changed while this person is signed in.
  useEffect(() => {
    if (!enabled) return undefined;
    window.addEventListener(CONTRACTS_PENDING_EVENT, check);
    return () => window.removeEventListener(CONTRACTS_PENDING_EVENT, check);
  }, [enabled, check]);

  const blocked = enabled && status !== "clear";
  const showPage = !enabled || pageMounted;

  const accept = () => {
    setSubmitting(true);
    setError(null);
    contractApi
      .accept(
        token,
        items.map((c) => c.contractVersionId)
      )
      .then((r) => {
        setSubmitting(false);
        setChecked({});
        const failure = r.ok ? null : r.message || t("contractAcceptFailed");
        // Reload the list (a contract may have changed meanwhile), then show why it failed.
        check().then(() => failure && setError(failure));
      });
  };

  const checkedCount = items.filter((c) => checked[c.contractVersionId]).length;
  const allChecked = items.length > 0 && checkedCount === items.length;

  let blocker = null;
  if (blocked && status === "loading") {
    blocker = <Overlay />;
  } else if (blocked) {
    blocker = (
      <div className="contract-gate" role="dialog" aria-modal="true" aria-labelledby="contract-gate-title">
        <header className="contract-gate__bar">
          <CusProLogo size={32} fill="#fff" />
          <div className="contract-gate__account">
            {user?.email && (
              <span className="contract-gate__email">{t("contractGateSignedInAs", { email: user.email })}</span>
            )}
            <button type="button" className="contract-gate__logout" onClick={logOut}>
              {t("logout")}
            </button>
          </div>
        </header>

        <main className="contract-gate__body">
          <Card className="default-card contract-gate__card">
            <h1 id="contract-gate-title" className="contract-gate__title">
              {t("contractsTitle")}
            </h1>

            {status === "error" ? (
              <>
                <p className="text-danger">{error}</p>
                <div className="contract-gate__actions">
                  <AdminButton onClick={check}>{t("contractRetry")}</AdminButton>
                </div>
              </>
            ) : (
              <>
                <p className="contract-gate__intro">{t("contractsPendingTitle")}</p>

                {groupByCompany(items).map((group) => (
                  <section key={group.companyId} className="contract-gate__company">
                    <h2 className="contract-gate__company-name">{group.companyName}</h2>
                    {group.items.map((c) => (
                      <article key={c.contractVersionId} className="contract-gate__item">
                        <h3 className="contract-gate__item-name">{c.name}</h3>
                        <SafeHtml className="contract-text contract-gate__text" html={c.content} />
                        <Form.Check
                          type="checkbox"
                          id={`contract-accept-${c.contractVersionId}`}
                          label={t("contractIAccept")}
                          checked={!!checked[c.contractVersionId]}
                          onChange={(e) =>
                            setChecked((prev) => ({ ...prev, [c.contractVersionId]: e.target.checked }))
                          }
                        />
                      </article>
                    ))}
                  </section>
                ))}

                {error && (
                  <div className="text-danger mb-2" role="alert">
                    {error}
                  </div>
                )}
                <div className="contract-gate__actions">
                  <span className="contract-gate__progress">
                    {allChecked ? "" : t("contractGateAllRequired", { done: checkedCount, total: items.length })}
                  </span>
                  <AdminButton onClick={accept} disabled={!allChecked || submitting}>
                    {t("contractAcceptAndContinue")}
                  </AdminButton>
                </div>
              </>
            )}
          </Card>
        </main>
      </div>
    );
  }

  return (
    <>
      {/* Always rendered, so blocking never remounts the page; `inert` keeps keyboard focus and
          screen readers out of it while the blocker is up. */}
      <div className="contract-gate-host" inert={blocked ? "" : undefined} aria-hidden={blocked || undefined}>
        {showPage ? children : null}
      </div>
      {blocker}
    </>
  );
}
